import { Component, OnInit } from "@angular/core";
import { UIState } from "../shared/state/ui.state";
import { DashboardService } from "../shared/services/dashboard.service";
import { DashboardMessage } from "../shared/models/dashboard.model";
import { switchMap, take, tap } from "rxjs/operators";
import { AppState } from "../shared/state/app.state";
import { AuthService } from "../auth/auth.service";
import { SettingsService } from "../shared/services/settings.service";
import { OperationType } from "../shared/models/user.model";
import { Router } from "@angular/router";
import { DashboardDetail } from "../shared/models/dashboard-detail.model";
import * as L from "leaflet";
import { InventoryCoordinate } from "../shared/models/inventory-coordinate.model";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import { checkedIcon, defaultIcon, dynamicIcon } from "../suspects/suspects-details/constant/defaultIcon";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit {
  loading: boolean = false;

  counters: DashboardDetail;
  coordinates: InventoryCoordinate[] = [];

  selectedMessageIndex = 0;
  messages: DashboardMessage[] = [{ Markdown: "Geen boodschappen beschikbaar." }];

  charts: any[] = [];

  moduleUnoccupied: boolean = false;
  moduleNeglected: boolean = false;
  moduleUninhabitable: boolean = false;
  moduleSecondaryResidence: boolean = false;

  private map: L.Map;

  constructor(
    public uiState: UIState,
    private dashboardService: DashboardService,
    public appState: AppState,
    private settingService: SettingsService,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.authService
      .hasScreenResource("DASHBOARD", OperationType.READ)
      .pipe(
        take(1),
        tap((hasPermission) => {
          if (!hasPermission || !this.settingService.getSettingValueAsBoolean("module-dashboard")) {
            this.router.navigateByUrl("/dossiers");
            return;
          }

          this.uiState.breadcrumbs$.next([
            {
              name: "Dashboard",
              routerLink: "/dashboard",
            },
          ]);

          this.moduleUnoccupied = Boolean(JSON.parse(this.settingService.getSettingByKey("module-leegstand")?.Value || "0"));
          this.moduleNeglected = Boolean(JSON.parse(this.settingService.getSettingByKey("module-verwaarlozing")?.Value || "0"));
          this.moduleUninhabitable = Boolean(
            JSON.parse(this.settingService.getSettingByKey("module-ongeschikt-onbewoonbaar")?.Value || "0"),
          );
          this.moduleSecondaryResidence = Boolean(
            JSON.parse(this.settingService.getSettingByKey("module-tweede-verblijf")?.Value || "0"),
          );

          this.fetchData();
        }),
      )
      .subscribe();
  }

  fetchData(): void {
    this.loading = true;

    this.dashboardService
      .getMessages()
      .pipe(
        take(1),
        tap((r) => {
          if (r.length > 0) {
            this.messages = r;
          }
        }),
        switchMap(() => this.dashboardService.getCounters()),
        tap((r) => {
          this.counters = r;
        }),
        switchMap(() => this.dashboardService.getInventoryCoordinates()),
        tap((coordinates) => {
          this.coordinates = coordinates;
        }),
        tap(() => {
          this.loading = false;
          setTimeout(() => {
            this.initMap();
          }, 1000);
        }),
      )
      .subscribe();
  }

  getPercentage(value: number, total: number): number {
    if (total === 0) {
      return 0;
    }
    const percentage = (value / total) * 100;
    return parseFloat(percentage.toFixed(2));
  }

  initMap(): void {
    if (this.map || this.loading) {
      return; // Prevent initialization if map exists or still loading
    }

    this.map = L.map("map").setView([50.8503, 4.3517], 8);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(this.map);
  }

  onToggleChange(event: MatButtonToggleChange) {
    const selectedValues: string[] = event.value;
    const coordinates = this.coordinates.filter(
      (coord) => selectedValues.includes(coord.Type) && coord.Latitude !== 0 && coord.Longitude !== 0,
    );

    this.updateMap(coordinates);
  }

  updateMap(coordinates: InventoryCoordinate[]): void {
    if (!this.map) {
      return;
    }

    this.map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        this.map.removeLayer(layer);
      }
    });

    const icon = dynamicIcon("#1c3243");
    coordinates.forEach((coord) => {
      if (coord.Latitude !== 0 && coord.Longitude !== 0) {
        L.marker([coord.Latitude, coord.Longitude], {icon}).addTo(this.map);
      }
    });

    if (coordinates.length > 0) {
      const bounds = L.latLngBounds(coordinates.map((coord) => [coord.Latitude, coord.Longitude]));
      this.map.fitBounds(bounds);
    }
  }
}
